import React, { useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  activateFacility,
  deactivateFacility,
  facilityActiveStatus,
  facilityId,
} from "reduxSlices/adminFacilitySlice"
import * as API from "services/api"
import { useCustomName } from "hooks"
import { FACILITY_KEY } from "utils/customNameHelpers"
import ContentBar from "shared/lists/ContentBar"
import Switch from "shared/Switch"
import { errorToast } from "shared/toast"

const activeDescription = (facilityCustomNameInSentence) => (
  `This ${facilityCustomNameInSentence} is active.  It will be presented as an option on forms.`
)

const deactivatedDescription = (facilityCustomNameInSentence) => (
  `This ${facilityCustomNameInSentence} is deactivated.  It will not be presented as an option on forms.`
)

const Active = () => {
  const id = useSelector(facilityId)
  const active = useSelector(facilityActiveStatus)

  const { nameInSentence: facilityCustomNameInSentence } = useCustomName(FACILITY_KEY)

  const dispatch = useDispatch()

  const activate = useCallback(async () => {
    const response = await API.activateFacility({ facilityId: id })

    if (response.ok) {
      dispatch(activateFacility())
    } else {
      errorToast(`Something went wrong. Unable to activate ${facilityCustomNameInSentence}.`)
    }
  }, [dispatch, id, facilityCustomNameInSentence])

  const deactivate = useCallback(async () => {
    const response = await API.deactivateFacility({ facilityId: id })

    if (response.ok) {
      dispatch(deactivateFacility())
    } else {
      errorToast(`Unable to deactivate ${facilityCustomNameInSentence}: ${response.data.errors.join(", ")}.`)
    }
  }, [dispatch, id, facilityCustomNameInSentence])

  const description = useMemo(() => (
    active
      ? activeDescription(facilityCustomNameInSentence)
      : deactivatedDescription(facilityCustomNameInSentence)
  ), [active, facilityCustomNameInSentence])

  return (
    <ContentBar
      description={description}
      title="Active status"
    >
      <Switch
        checked={active}
        onChange={active ? deactivate : activate}
      />
    </ContentBar>
  )
}

export default Active
